import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import {
  CONTENT_ENGINE_ADVANCED_QUESTIONS,
  scoreContentEngineAdvanced,
} from "@/lib/content-engine-advanced";
import { ResultCard } from "@/components/tools/ResultCard";
import { DarkSectionBackdrop } from "@/components/DarkSectionBackdrop";

export const Route = createFileRoute("/tools/content-engine-advanced")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://scottcbusiness.com/tools/content-engine-advanced" },
      { name: "twitter:title", content: "Content Engine Pro Audit | Scott Cunningham" },
      { name: "twitter:description", content: "A 20-question advanced diagnostic for high-performing content teams. Tests brand depth, AI leverage, distribution, and attribution." },

      { title: "Content Engine Pro Audit | Scott Cunningham" },
      {
        name: "description",
        content:
          "A 20-question advanced diagnostic for high-performing content teams. Tests brand depth, AI leverage, distribution, and attribution.",
      },
      { property: "og:title", content: "Content Engine Pro Audit" },
      {
        property: "og:description",
        content:
          "An advanced diagnostic for teams already running a real content engine. Tests the deeper layers most operators are missing.",
      },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/tools/content-engine-advanced" }],
  }),
  component: ContentEngineAdvancedPage,
});

function ContentEngineAdvancedPage() {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const allAnswered = CONTENT_ENGINE_ADVANCED_QUESTIONS.every((q) => answers[q.id]);
  const result = useMemo(
    () => (submitted ? scoreContentEngineAdvanced(answers) : null),
    [submitted, answers],
  );

  async function handleSubmit() {
    if (!allAnswered || loading) return;
    setLoading(true);
    const computed = scoreContentEngineAdvanced(answers);
    const startedAt = Date.now();
    try {
      await fetch("/api/public/tool-submission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool: "content-engine-advanced",
          score: computed.percentage,
          level: computed.level.key,
          inputSummary: `total=${computed.total}/80`,
          consumeSlot: false,
        }),
      });
    } catch {
      /* still show locally */
    }
    const elapsed = Date.now() - startedAt;
    if (elapsed < 2200) await new Promise((r) => setTimeout(r, 2200 - elapsed));
    setSubmitted(true);
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="relative isolate overflow-hidden bg-foreground text-cream">
        <DarkSectionBackdrop />
        <div className="relative mx-auto max-w-3xl px-6 py-16 sm:py-20">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link
              to="/tools"
              className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-cream/70 hover:text-cream"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All tools
            </Link>
            <div className="inline-flex items-center gap-2 rounded-full border border-cream/20 bg-cream/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-cream/80">
              Advanced · 20 questions
            </div>
          </div>
          <h1 className="font-display mt-4 text-4xl text-cream sm:text-5xl">
            Content Engine <span className="text-gradient">Pro Audit</span>
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-cream/75">
            The deeper diagnostic. Built for teams who already run a real content system and want to
            know which advanced layers are real, which are aspirational, and what's left to harden
            before the engine can scale or be handed off.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-12">
        {submitted && result ? (
          <ResultCard
            tool="content-engine-advanced"
            score={result.percentage}
            levelLabel={result.level.label}
            diagnosis={result.level.diagnosis}
            gaps={result.gaps}
            nextStep={result.level.nextStep}
            cta={result.level.cta}
          />
        ) : (
          <div className="space-y-8">
            {CONTENT_ENGINE_ADVANCED_QUESTIONS.map((q, i) => (
              <fieldset
                key={q.id}
                className="rounded-2xl border border-border bg-card p-6"
                style={{ boxShadow: "var(--shadow-soft)" }}
              >
                <legend className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Q{i + 1}
                </legend>
                <p className="font-display text-xl text-ink">{q.question}</p>
                <div className="mt-4 grid gap-2">
                  {q.options.map((opt, idx) => {
                    const points = idx + 1;
                    const selected = answers[q.id] === points;
                    return (
                      <label
                        key={opt}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition ${
                          selected
                            ? "border-primary bg-primary/5 text-ink"
                            : "border-border bg-background text-ink/80 hover:border-ink/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={points}
                          checked={selected}
                          onChange={() => setAnswers((a) => ({ ...a, [q.id]: points }))}
                          className="sr-only"
                        />
                        <span
                          className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${selected ? "border-primary bg-primary" : "border-border"}`}
                        >
                          {selected && (
                            <span className="h-2 w-2 rounded-full bg-primary-foreground" />
                          )}
                        </span>
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}

            <div
              className="sticky bottom-4 z-10 rounded-2xl border border-border bg-card p-4 backdrop-blur"
              style={{ boxShadow: "var(--shadow-soft)" }}
            >
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <p className="text-sm text-muted-foreground">
                  {Object.keys(answers).length}/{CONTENT_ENGINE_ADVANCED_QUESTIONS.length} answered
                </p>
                <button
                  onClick={handleSubmit}
                  disabled={!allAnswered || loading}
                  className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? "Running the deep audit…" : "Get my pro audit"}
                </button>
              </div>
            </div>

            <p className="text-xs italic text-muted-foreground">
              This is the advanced diagnostic. It's built for teams already operating a real content
              engine. A perfect score takes real depth, not just good intentions.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
